import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import { Button } from '../ui/Button';
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import apiClient from '../../utils/api';

// Log reminders via Web Push (Phase N) — self-generated VAPID, no vendor. The server
// pushes at the user's chosen times (skipping ones they've already logged near); this
// screen handles device permission/subscription + the reminder schedule.

const pushSupported = typeof window !== 'undefined'
  && 'serviceWorker' in navigator
  && 'PushManager' in window
  && 'Notification' in window
  && (window.isSecureContext ?? false);

// Convert the URL-safe base64 VAPID public key to the Uint8Array pushManager wants.
const urlB64ToUint8Array = (base64) => {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(b64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
};

const localTimezone = () => {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'; }
  catch (e) { return 'UTC'; }
};

const ReminderSettings = () => {
  const [enabled, setEnabled] = useState(false);
  const [times, setTimes] = useState(['09:00', '20:00']);
  const [skipIfLogged, setSkipIfLogged] = useState(true);
  const [vapidKey, setVapidKey] = useState(null);
  const [permission, setPermission] = useState(pushSupported ? Notification.permission : 'unsupported');
  const [subscribed, setSubscribed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // Health-change alerts. A separate switch from the daily reminders on
  // purpose: this is the rare one you asked to be told about, and silencing
  // your evening nudge shouldn't silence it too.
  const [anomalyAlerts, setAnomalyAlerts] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [prefRes, keyRes] = await Promise.all([
          apiClient.get('/api/notifications/preferences'),
          apiClient.get('/api/notifications/vapid-public-key'),
        ]);
        if (cancelled) return;
        const p = prefRes.data;
        setEnabled(p.enabled);
        if (Array.isArray(p.times) && p.times.length) setTimes(p.times);
        setSkipIfLogged(p.skip_if_logged);
        setAnomalyAlerts(p.anomaly_alerts !== false);
        setVapidKey(keyRes.data.public_key || null);
        if (pushSupported) {
          // `serviceWorker.ready` never rejects and never resolves when no
          // worker is registered — it just waits forever. Awaiting it bare left
          // this whole section stuck on "Loading…" with no error and no
          // controls, which is what happens on the dev server (CRA registers a
          // worker only in production builds) and would happen in production
          // too if registration were slow or failed. Knowing whether a
          // subscription already exists is a nicety; rendering the settings is
          // not, so it gets a deadline and we carry on without it.
          const reg = await Promise.race([
            navigator.serviceWorker.ready,
            new Promise((resolve) => setTimeout(() => resolve(null), 3000)),
          ]);
          if (reg) {
            const sub = await reg.pushManager.getSubscription();
            // Re-register whatever this device already has, every time.
            //
            // The browser's copy of a subscription and the server's row can
            // drift apart, and when they do nothing recovers on its own: the
            // server prunes its row the moment a push bounces (a rotated
            // endpoint, a reinstall, a phone offline long enough to be dropped)
            // while the browser happily keeps reporting a live subscription. The
            // UI then shows a device as connected, every reminder silently
            // reaches nobody, and "Send a test" answers "No device is subscribed
            // yet" with no button anywhere that fixes it.
            //
            // /subscribe is an idempotent upsert keyed on the endpoint, so
            // re-posting a subscription we already have costs one request and
            // heals that split permanently. Only claim `subscribed` if the
            // server accepted it — this flag's whole job is to say whether the
            // *server* can reach us.
            if (sub) {
              const json = sub.toJSON();
              try {
                await apiClient.post('/api/notifications/subscribe', {
                  endpoint: json.endpoint,
                  keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
                  user_agent: (navigator.userAgent || '').slice(0, 400),
                });
                if (!cancelled) setSubscribed(true);
              } catch {
                if (!cancelled) setSubscribed(false);
              }
            } else if (!cancelled) {
              setSubscribed(false);
            }
          }
        }
      } catch (e) {
        // Non-fatal — the section just shows defaults.
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const ensureSubscribed = useCallback(async () => {
    if (!pushSupported) { toast.error('Reminders need the installed app on your phone.'); return false; }
    if (!vapidKey) { toast.error('Reminders aren’t configured on the server yet.'); return false; }
    const perm = await Notification.requestPermission();
    setPermission(perm);
    if (perm !== 'granted') {
      toast.error('Notifications are blocked. Turn them on in your browser settings.');
      return false;
    }
    // Same deadline as the load path, and for the same reason: with no worker
    // registered this waits forever, which strands the user immediately after
    // they granted permission — the prompt appears, then nothing ever happens
    // and no error is shown. Say what went wrong instead of hanging.
    const reg = await Promise.race([
      navigator.serviceWorker.ready,
      new Promise((resolve) => setTimeout(() => resolve(null), 8000)),
    ]);
    if (!reg) {
      toast.error('The app’s background worker isn’t running yet. Close and reopen the app, then try again.');
      return false;
    }
    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlB64ToUint8Array(vapidKey),
      });
    }
    const json = sub.toJSON();
    await apiClient.post('/api/notifications/subscribe', {
      endpoint: json.endpoint,
      keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
      user_agent: (navigator.userAgent || '').slice(0, 400),
    });
    setSubscribed(true);
    return true;
  }, [vapidKey]);

  const save = async () => {
    const clean = [...new Set(times.filter((t) => /^([01]\d|2[0-3]):[0-5]\d$/.test(t)))].sort();
    if (enabled && clean.length === 0) { toast.error('Add at least one reminder time.'); return; }
    setSaving(true);
    try {
      // Either kind needs a live device subscription — health alerts can be on
      // while daily reminders are off, so this can't key off `enabled` alone.
      if ((enabled || anomalyAlerts) && !subscribed) {
        const ok = await ensureSubscribed();
        if (!ok) { setSaving(false); return; }
      }
      await apiClient.put('/api/notifications/preferences', {
        enabled,
        times: clean,
        timezone: localTimezone(),
        skip_if_logged: skipIfLogged,
        anomaly_alerts: anomalyAlerts,
      });
      setTimes(clean.length ? clean : times);
      toast.success(enabled ? 'Reminders saved.' : 'Reminders turned off.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Couldn’t save reminders.');
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    try {
      if (!subscribed) { const ok = await ensureSubscribed(); if (!ok) return; }
      await apiClient.post('/api/notifications/test');
      toast.success('Test sent — check your notifications.');
    } catch (e) {
      // "No device is subscribed yet" from the server while we believed we were
      // subscribed means the two sides disagree. Rather than report a dead end
      // the user can do nothing about, register this device and try once more.
      if (e.response?.status === 400) {
        try {
          const ok = await ensureSubscribed();
          if (ok) {
            await apiClient.post('/api/notifications/test');
            toast.success('Test sent — check your notifications.');
            return;
          }
        } catch { /* fall through to the message below */ }
      }
      toast.error(e.response?.data?.detail || 'Couldn’t send a test notification.');
    }
  };

  const setTimeAt = (i, v) => setTimes((prev) => prev.map((t, j) => (j === i ? v : t)));
  const removeTimeAt = (i) => setTimes((prev) => prev.filter((_, j) => j !== i));
  const addTime = () => setTimes((prev) => [...prev, '12:00']);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Log reminders</CardTitle>
      </CardHeader>
      <CardContent className="p-6 pt-0 space-y-5">
        <p className="text-sm text-gray-600 dark:text-gray-300">
          Get a gentle nudge to log at the times you choose. Once you’ve finished the day’s
          log, it stays quiet — so it only nudges when something’s actually left to log.
        </p>

        {!pushSupported && (
          <div className="rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 px-4 py-3 text-sm text-amber-800 dark:text-amber-200">
            Reminders work in the installed app on your phone (they need a secure connection).
            Add Vegetablah to your home screen, open it, then set them up here.
          </div>
        )}

        {loading ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">Loading…</p>
        ) : (
          <>
            <label className="flex items-center gap-3">
              <input
                type="checkbox"
                checked={enabled}
                onChange={(e) => setEnabled(e.target.checked)}
                className="h-5 w-5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                disabled={!pushSupported}
              />
              <span className="text-sm font-medium text-gray-900 dark:text-white">
                Remind me to log
              </span>
            </label>

            {enabled && (
              <>
                <div className="space-y-2">
                  <span className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Reminder times
                  </span>
                  {times.map((t, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <input
                        type="time"
                        value={t}
                        onChange={(e) => setTimeAt(i, e.target.value)}
                        className="h-10 rounded-md border border-gray-300 dark:border-gray-600 bg-transparent px-3 text-sm text-gray-900 dark:text-gray-100"
                      />
                      {times.length > 1 && (
                        <Button type="button" variant="ghost" size="sm" onClick={() => removeTimeAt(i)} title="Remove">
                          <TrashIcon className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                  {times.length < 12 && (
                    <Button type="button" variant="outline" size="sm" onClick={addTime} className="flex items-center">
                      <PlusIcon className="w-4 h-4 mr-1.5" /> Add a time
                    </Button>
                  )}
                </div>

                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={skipIfLogged}
                    onChange={(e) => setSkipIfLogged(e.target.checked)}
                    className="h-5 w-5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    Don’t remind me once today’s log is complete
                  </span>
                </label>
              </>
            )}

            {/* Outside the `enabled` block on purpose: health alerts are a
                separate kind, and you can want them without wanting a daily
                nudge. Defaults on — the detector raises these roughly monthly
                at most, and being told is the entire point of the feature. */}
            <div className="border-t border-surface-border dark:border-surface-dark-border pt-4 space-y-2">
              <label className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={anomalyAlerts}
                  onChange={(e) => setAnomalyAlerts(e.target.checked)}
                  className="h-5 w-5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  Tell me when my health signals change
                </span>
              </label>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Rare by design — only when several of your measurements shift
                together for a couple of weeks, in either direction.
              </p>
            </div>

            <div className="flex flex-wrap gap-3 pt-1">
              <Button type="button" onClick={save} disabled={saving || !pushSupported}>
                {saving ? 'Saving…' : 'Save reminders'}
              </Button>
              {pushSupported && (
                <Button type="button" variant="outline" onClick={sendTest} disabled={saving}>
                  Send a test
                </Button>
              )}
            </div>
            {permission === 'denied' && (
              <p className="text-xs text-red-600 dark:text-red-400">
                Notifications are blocked for this site. Re-enable them in your browser settings to use reminders.
              </p>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default ReminderSettings;
